import type { RasterSelectionCommand } from "../history/historyTypes";
import { estimateRasterSelectionCommandBytes } from "./selectionMemory";
import { createRasterPixelCanvas } from "./RasterPixelBuffer";
import type { ImmutableRasterPixelBuffer } from "./RasterPixelBuffer";
import type {
  IntegerRect,
  RasterSelectionAction,
  RasterSelectionTransform,
} from "./selectionTypes";

let commandSequence = 0;

function immutableRect(rect: Readonly<IntegerRect>): IntegerRect {
  return Object.freeze({ ...rect });
}

function immutableTransform(
  transform: Readonly<RasterSelectionTransform>,
): RasterSelectionTransform {
  return Object.freeze({ ...transform, centre: Object.freeze({ ...transform.centre }) });
}

export function createRasterSelectionCommand(options: {
  action: RasterSelectionAction;
  layerId: string;
  sourceRect: Readonly<IntegerRect>;
  sourcePixels: ImmutableRasterPixelBuffer;
  destination?: Readonly<RasterSelectionTransform>;
  clearSource: boolean;
  clipRect: Readonly<IntegerRect>;
}): RasterSelectionCommand {
  if (
    options.sourcePixels.width !== options.sourceRect.width ||
    options.sourcePixels.height !== options.sourceRect.height
  ) {
    throw new Error("Selected pixel dimensions do not match their source rectangle.");
  }
  const estimatedBytes = estimateRasterSelectionCommandBytes(options.sourcePixels.byteLength);
  if (!Number.isFinite(estimatedBytes)) {
    throw new Error("The selection command memory estimate is invalid.");
  }
  commandSequence += 1;
  return Object.freeze({
    kind: "rasterSelection" as const,
    id: `raster-selection-${commandSequence}`,
    action: options.action,
    layerId: options.layerId,
    sourceRect: immutableRect(options.sourceRect),
    sourcePixels: options.sourcePixels,
    destination: options.destination ? immutableTransform(options.destination) : undefined,
    clearSource: options.clearSource,
    clipRect: immutableRect(options.clipRect),
    estimatedBytes,
  });
}

export function applyRasterSelectionCommandToContext(
  context: CanvasRenderingContext2D,
  command: Readonly<RasterSelectionCommand>,
): void {
  context.save();
  try {
    context.beginPath();
    context.rect(
      command.clipRect.x,
      command.clipRect.y,
      command.clipRect.width,
      command.clipRect.height,
    );
    context.clip();
    if (command.clearSource) {
      context.clearRect(
        command.sourceRect.x,
        command.sourceRect.y,
        command.sourceRect.width,
        command.sourceRect.height,
      );
    }
    if (command.destination && command.sourcePixels.hasVisiblePixels) {
      const source = createRasterPixelCanvas(command.sourcePixels);
      try {
        drawRasterSelectionSource(context, source, command.destination);
      } finally {
        source.width = 1;
        source.height = 1;
      }
    }
  } finally {
    context.restore();
  }
}

export function drawRasterSelectionSource(
  context: CanvasRenderingContext2D,
  source: CanvasImageSource,
  destination: Readonly<RasterSelectionTransform>,
): void {
  context.save();
  try {
    context.translate(destination.centre.x, destination.centre.y);
    context.rotate((destination.rotation * Math.PI) / 180);
    context.scale(destination.flipX ? -1 : 1, destination.flipY ? -1 : 1);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(
      source,
      -destination.width / 2,
      -destination.height / 2,
      destination.width,
      destination.height,
    );
  } finally {
    context.restore();
  }
}
